/**
 * HTTP client for the bevi-ailab API.
 *
 * Attaches the auth bearer token from tokenStore (or X-Dev-User in dev-mode).
 * 401 triggers `notifyAuthError()` so the AuthProvider can redirect to login.
 * 403 (allowlist denial) bubbles up as ApiClientError — callers render inline.
 */

import { devEmail, isDevMode } from '../auth/config';
import { getAccessToken, notifyAuthError } from '../auth/tokenStore';

const API_BASE = '/api/v1';

export interface ApiError {
  status: number;
  message: string;
  error?: string;
  retryAfterSeconds?: number;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly error: ApiError;
  constructor(status: number, error: ApiError) {
    super(error.message || error.error || `HTTP ${status}`);
    this.status = status;
    this.error = error;
    this.name = 'ApiClientError';
  }
}

async function buildHeaders(contentType?: string): Promise<HeadersInit> {
  const headers: Record<string, string> = {};
  if (isDevMode()) {
    const email = devEmail();
    if (email) headers['X-Dev-User'] = email;
  } else {
    const token = await getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (contentType) headers['Content-Type'] = contentType;
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) notifyAuthError();
    let body: ApiError;
    try {
      body = await response.json();
    } catch {
      body = { status: response.status, message: response.statusText };
    }
    throw new ApiClientError(response.status, {
      status: response.status,
      message: body.message || body.error || response.statusText,
      error: body.error,
      retryAfterSeconds: body.retryAfterSeconds,
    });
  }
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { method: 'GET', headers: await buildHeaders() });
  return handleResponse<T>(response);
}

export async function apiPost<T, B = unknown>(path: string, body?: B, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: await buildHeaders('application/json'),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  return handleResponse<T>(response);
}

export async function apiPut<T, B = unknown>(path: string, body: B, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: await buildHeaders('application/json'),
    body: JSON.stringify(body),
    signal,
  });
  return handleResponse<T>(response);
}

/**
 * DELETE authenticated by the browser's cookies alone: no bearer / dev header is attached, so it works after
 * the bearer has expired, and the raw response is returned without the 401 → login redirect. Only for
 * `DELETE /sessions/current`, the one mutating route the server lets the session cookie authenticate.
 */
export async function apiDeleteWithCookie(path: string, signal?: AbortSignal): Promise<Response> {
  return fetch(`${API_BASE}${path}`, { method: 'DELETE', credentials: 'same-origin', signal });
}

/** DELETE with the bearer / dev header; parses the JSON body, if any. */
export async function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: await buildHeaders(),
    signal,
  });
  return handleResponse<T>(response);
}
